import type { NoteMetadataSnapshot } from '@/schemas/note-metadata.schema';
import type { EncounterSessionTiming } from '@/lib/encounterSessionTiming';
import type { ParticipantAttendanceRow } from '../components/ParticipantAttendanceSection';
import { formatShortDate } from './dateFormatters';
import {
  getParticipantAttendanceStatusLabel,
  type ParticipantAttendanceFhirStatus,
} from './participantAttendance';

function formatClockTime(iso: string | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return iso;
  }
}

function isHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value.trim());
}

function resolveSessionEndIso(metadata: NoteMetadataSnapshot): string | undefined {
  if (metadata.end?.trim()) return metadata.end;
  if (!metadata.start || metadata.durationMinutes == null) return undefined;
  try {
    const startMs = new Date(metadata.start).getTime();
    if (Number.isNaN(startMs)) return undefined;
    return new Date(startMs + metadata.durationMinutes * 60_000).toISOString();
  } catch {
    return undefined;
  }
}

/** Zoom-derived minutes carry decimals; show at most one. */
function formatMinutes(minutes: number): string {
  return `${Math.round(minutes * 10) / 10} min`;
}

/** Measured session length from the Encounter timing extension, with the scheduled block for contrast. */
function buildActualDurationPhrase(timing: EncounterSessionTiming | undefined): string {
  if (timing?.actualSessionMinutes == null) return '';
  const actual = formatMinutes(timing.actualSessionMinutes);
  const scheduled =
    timing.appointmentExpectedMinutes != null
      ? ` (scheduled ${formatMinutes(timing.appointmentExpectedMinutes)})`
      : '';
  return `Actual session duration: ${actual}${scheduled}. `;
}

/**
 * Deterministic session summary prose for the progress note header.
 * Session timing first, then attendees (status + location) grouped after.
 */
export function buildSessionMetadataSummaryText(input: {
  metadata: NoteMetadataSnapshot;
  attendanceRows?: ParticipantAttendanceRow[];
  attendanceByPatientId?: Record<string, ParticipantAttendanceFhirStatus | undefined>;
  locationByPatientId?: Record<string, string | undefined>;
  sessionTiming?: EncounterSessionTiming;
}): string {
  const {
    metadata,
    attendanceRows = [],
    attendanceByPatientId = {},
    locationByPatientId = {},
    sessionTiming,
  } = input;

  const date = metadata.start ? formatShortDate(metadata.start) : '—';
  const start = formatClockTime(metadata.start);
  const end = formatClockTime(resolveSessionEndIso(metadata));

  const attendees =
    attendanceRows.length > 0
      ? attendanceRows
          .map((row) => {
            const status = attendanceByPatientId[row.patientId];
            const statusLabel = status ? getParticipantAttendanceStatusLabel(status) : 'unconfirmed';
            return `${row.displayName} (${statusLabel})`;
          })
          .join('; ')
      : 'no client attendance recorded';

  const locations =
    attendanceRows.length > 0
      ? attendanceRows
          .map((row) => {
            const loc = locationByPatientId[row.patientId]?.trim();
            if (!loc || isHttpUrl(loc)) return null;
            return `${row.displayName} — ${loc}`;
          })
          .filter((v): v is string => Boolean(v))
          .join('; ')
      : '';

  const locationPhrase =
    locations
    || (metadata.locations ?? [])
      .map((l) => l.trim())
      .filter((l) => l && !isHttpUrl(l))
      .join('; ')
    || 'unspecified';

  return (
    `Session ${start} – ${end} on ${date}. ` +
    buildActualDurationPhrase(sessionTiming) +
    `Attendees: ${attendees}. ` +
    `Locations: ${locationPhrase}.`
  );
}
